import React, { useEffect } from 'react';
import {
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Link,
    Card,
    CardContent
} from '@mui/material';
import {
    OpenInNew as OpenInNewIcon
} from '@mui/icons-material';

const TransportLayerComponent = ({
    transportDevices = [],
    selectedTransportDevice = '',
    selectedTransportStream = '',
    onTransportDeviceChange,
    onTransportStreamChange,
    isOwner = false,
    isAdmin = false,
    disabled = false
}) => {
    const getTransportStreams = (device) => {
        if (!device || !device.transport_layer_config) return [];

        try {
            const config = typeof device.transport_layer_config === 'string'
                ? JSON.parse(device.transport_layer_config)
                : device.transport_layer_config;
            return Array.isArray(config) ? config : [];
        } catch (e) {
            return [];
        }
    };

    const getSelectedTransportDevice = () => {
        return transportDevices.find(td => td.id.toString() === selectedTransportDevice);
    };

    // Validation effect - disable form submission if no stream is selected
    useEffect(() => {
        if (disabled) {
            return;
        }
        const submitButton = document.getElementById('submit-btn');
        const isValidSelection = selectedTransportDevice && selectedTransportStream;

        if (submitButton) {
            // Add transport validation attribute
            submitButton.setAttribute('data-transport-valid', isValidSelection ? 'true' : 'false');

            // Check all validation attributes to determine if submit should be enabled
            const allValidationAttrs = [
                submitButton.getAttribute('data-transport-valid'),
                // Add other validation attributes here if needed
            ];

            const allValid = allValidationAttrs.every(attr => attr === 'true' || attr === null);
            submitButton.disabled = !allValid;
        }

        // Also prevent form submission via form validation
        const form = document.querySelector('form');
        if (form) {
            const handleSubmit = (e) => {
                if (!isValidSelection) {
                    e.preventDefault();
                    alert('Please select a transport device and stream before saving.');
                    return false;
                }
            };

            // Remove existing listener to avoid duplicates
            form.removeEventListener('submit', handleSubmit);
            form.addEventListener('submit', handleSubmit);
            return () => {
                form.removeEventListener('submit', handleSubmit);
                submitButton?.removeAttribute('data-transport-valid');
            };
        }
    }, [disabled, selectedTransportDevice, selectedTransportStream]);

    const selectedDevice = getSelectedTransportDevice();
    const streams = selectedDevice ? getTransportStreams(selectedDevice) : [];
    const hasValidSelection = selectedTransportDevice && selectedTransportStream;

    return (
        <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                    Transport Layer Configuration
                </Typography>

                {/* Validation warning */}
                {!hasValidSelection && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Required:</strong> You must select both a transport device and a stream to save changes.
                        </Typography>
                    </Alert>
                )}

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Select Transport Device</InputLabel>
                    <Select
                        value={selectedTransportDevice}
                        onChange={(e) => onTransportDeviceChange && onTransportDeviceChange(e.target.value)}
                        disabled={disabled}
                        label="Select Transport Device"
                        required
                        error={!selectedTransportDevice}
                    >
                        <MenuItem value="">
                            <em>Select a device</em>
                        </MenuItem>
                        {transportDevices.map((device) => (
                            <MenuItem key={device.id} value={device.id.toString()}>
                                {device.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {!selectedTransportDevice && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                            Transport device selection is required
                        </Typography>
                    )}
                </FormControl>

                {selectedTransportDevice && (
                    <>
                        {streams.length === 0 ? (
                            <Alert severity="info">
                                No streams configured for this transport device.
                                {(isOwner || isAdmin) && (
                                    <>
                                        {' '}
                                        <Link
                                            href={`/devices/${selectedDevice.id}/edit`}
                                            target="_blank"
                                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                        >
                                            Configure streams
                                            <OpenInNewIcon fontSize="small" />
                                        </Link>
                                    </>
                                )}
                            </Alert>
                        ) : (
                            <FormControl fullWidth size="small">
                                <InputLabel>Select Stream</InputLabel>
                                <Select
                                    value={selectedTransportStream}
                                    onChange={(e) => onTransportStreamChange && onTransportStreamChange(e.target.value)}
                                    disabled={disabled}
                                    label="Select Stream"
                                    required
                                    error={selectedTransportDevice && !selectedTransportStream}
                                >
                                    <MenuItem value="">
                                        <em>Select a stream</em>
                                    </MenuItem>
                                    {streams.map((stream, index) => (
                                        <MenuItem key={index} value={stream.url}>
                                            {stream.name} - {stream.url}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {selectedTransportDevice && !selectedTransportStream && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                        Stream selection is required
                                    </Typography>
                                )}
                            </FormControl>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default TransportLayerComponent;
